import { Component, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { openClose } from '@core/animations/animations';
import { BusinessAddress, BusinessUser } from '@models/businesses';
import {
	ActingForType,
	MatterBusinessUser,
	MatterFileType,
	MatterStatus,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { select, Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { BusinessUsersDispatch } from '@state/business-users/business-users.dispatch';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessAddressesState } from '@state/business/address';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { LeapLawyerClerkService } from 'src/app/public/leap/leap-lawyer-clerk-service';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

const MatterStatusOptions = [
	{ label: 'Active', id: MatterStatus.ACTIVE },
	{ label: 'Closed', id: MatterStatus.CLOSED },
	{ label: 'Inactive', id: MatterStatus.INACTIVE },
];

@UntilDestroy()
@Component({
	selector: 'app-matter-details',
	templateUrl: './edit-matter-details.component.html',
	animations: [openClose()],
	standalone: false,
})
export class EditMatterDetailsComponent implements OnInit {
	public fileName: string;
	public fileNumber: string;
	public fileAccountingNumber: string;
	public status: MatterStatus;
	public office: string;
	public actingFor: ActingForType;
	public matterStatusOptions = MatterStatusOptions;
	public actingForTypes = ActingForType;
	public fileTypes = MatterFileType;
	public lawyerOnMatter: string;
	public clerkOnMatter: string;

	@Select(SelectedMatter.matterName) fileName$: Observable<string>;
	@Select(SelectedMatter.matterNumber) fileNumber$: Observable<string>;
	@Select(SelectedMatter.matterAccountingNumber)
	fileAccountingNumber$: Observable<string>;
	@Select(SelectedMatter.status) status$: Observable<MatterStatus>;
	@SelectSnapshot(SelectedMatter.office) matterOffice: BusinessAddress;
	@SelectSnapshot(BusinessAddressesState.getAll) offices: BusinessAddress[];
	@Select(SelectedMatter.actingFor) actingFor$: Observable<ActingForType>;
	@SelectSnapshot(BusinessUsersState.getLawyers) lawyers: BusinessUser[];
	@SelectSnapshot(BusinessUsersState.getClerks) clerks: BusinessUser[];
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@ViewSelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@SelectSnapshot(SelectedMatter.activeAssignedLawyer)
	assignedLawyer: MatterBusinessUser;
	@SelectSnapshot(SelectedMatter.activeAssignedClerk)
	assignedClerk: MatterBusinessUser;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	public loggedInUser_ = select(BusinessUsersState.getLoggedInBusinessUser);

	constructor(
		private matterDispatch: MatterDispatch,
		private analytics: AnalyticsService,
		private leapLawyerClerkService: LeapLawyerClerkService,
		private drawerService: DrawerService,
		private businessUsersDispatch: BusinessUsersDispatch,
	) {}

	ngOnInit(): void {
		this.fileName$.pipe(untilDestroyed(this)).subscribe((_fileName) => {
			this.fileName = _fileName;
		});

		this.fileNumber$.pipe(untilDestroyed(this)).subscribe((_fileNumber) => {
			this.fileNumber = _fileNumber;
		});

		this.fileAccountingNumber$
			.pipe(untilDestroyed(this))
			.subscribe((_fileAccountingNumber) => {
				this.fileAccountingNumber = _fileAccountingNumber;
			});

		this.status$.pipe(untilDestroyed(this)).subscribe((_status) => {
			this.status = _status;
		});

		this.actingFor$.pipe(untilDestroyed(this)).subscribe((_actingFor) => {
			this.actingFor = _actingFor;
		});

		if (this.matterOffice) {
			this.office = this.matterOffice.id;
		}

		if (this.assignedLawyer) {
			this.lawyerOnMatter = this.assignedLawyer.id;
		}

		if (this.assignedClerk) {
			this.clerkOnMatter = this.assignedClerk.id;
		}
	}

	onSubmit(form: NgForm) {
		if (form.valid) {
			const selectedOffice = this.offices.find((x) => x.id === this.office);

			this.matterDispatch.SetFileNameAndNumber(
				this.matterId,
				this.fileName,
				this.fileNumber,
				this.status,
				selectedOffice.id,
				this.actingFor ?? null,
				this.fileAccountingNumber ?? null,
			);

			this.setLawyer();

			// If 'None selected' option is chosen, reset the clerk
			this.clerkOnMatter === 'None' ||
			this.clerkOnMatter === 'undefined' ||
			this.clerkOnMatter === null
				? this.resetClerk()
				: this.setClerk();

			this.updateAssociatedOfficeIdOnBusinessUser(selectedOffice.id);

			this.drawerService.closeDrawer();
		}
	}

	setStatus($event: MatterStatus) {
		if ($event === MatterStatus.ACTIVE) {
			this.analytics.logEvent(
				AnalyticsEvent.FILE_DETAILS_MODAL_FILE_STATUS_ACTIVE_CLICK,
				{ matterId: this.matterId },
			);
		}
		if ($event === MatterStatus.CLOSED) {
			this.analytics.logEvent(
				AnalyticsEvent.FILE_DETAILS_MODAL_FILE_STATUS_CLOSED_CLICK,
				{ matterId: this.matterId },
			);
		}
		if ($event === MatterStatus.INACTIVE) {
			this.analytics.logEvent(
				AnalyticsEvent.FILE_DETAILS_MODAL_FILE_STATUS_INACTIVE_CLICK,
				{ matterId: this.matterId },
			);
		}
		this.status = $event;
	}

	getFullNameFromBusinessUser(user: BusinessUser): string {
		return `${user.firstName} ${user.lastName}`;
	}

	setLawyer(): void {
		const selectedLawyerId = this.lawyerOnMatter;
		const lawyer = this.lawyers.find((x) => x.id === selectedLawyerId);

		this.matterDispatch.SetAssignedLawyer(
			this.matterId,
			lawyer.id,
			lawyer.firstName,
			lawyer.lastName,
		);

		if (this.isLeapMatter) {
			this.leapLawyerClerkService.syncMatterUser(
				this.matterId,
				lawyer,
				true,
				false,
			);
		}
	}

	setClerk(): void {
		const selectedClerkId = this.clerkOnMatter;

		if (!selectedClerkId) return;

		const clerk = this.clerks.find((x) => x.id === selectedClerkId);

		if (!clerk) return;

		this.matterDispatch.SetAssignedClerk(
			this.matterId,
			clerk.id,
			clerk.firstName,
			clerk.lastName,
		);

		if (this.isLeapMatter) {
			this.leapLawyerClerkService.syncMatterUser(this.matterId, clerk, false);
		}
	}

	resetClerk(): void {
		this.matterDispatch.SetAssignedClerk(this.matterId, '', '', '');
	}

	updateAssociatedOfficeIdOnBusinessUser(officeId: string) {
		if (!officeId) return;

		const loggedInUser = this.loggedInUser_();

		if (loggedInUser.associatedOfficeId === officeId) return;

		const userIsLawyerOrClerkOnMatter =
			loggedInUser.id === this.lawyerOnMatter ||
			loggedInUser.id === this.clerkOnMatter;

		if (userIsLawyerOrClerkOnMatter) {
			this.businessUsersDispatch.SetAssociatedOfficeId(officeId, loggedInUser);
		}
	}

	get showOfficialOfficeName() {
		if (!this.office) return false;

		const selectedOffice = this.offices.find((x) => x.id === this.office);
		return !!selectedOffice.officeNickName;
	}

	get officialOfficeName() {
		const selectedOffice = this.offices.find((x) => x.id === this.office);
		return selectedOffice?.officeName || '';
	}
}
